import { useEffect, useState } from 'react';

import { useUserContext } from '@/modules/auth/hooks/useUser';
import { JobContentFragment } from '@/modules/corporate/modules/bulk-import/graphql/fragments/generated/JobContent';
import { JobStateFragment } from '@/modules/corporate/modules/bulk-import/graphql/fragments/generated/JobState';
import { useJobQuery } from '@/modules/corporate/modules/bulk-import/graphql/queries/generated/job';
import { useJobByCreatorSubscription } from '@/modules/corporate/modules/bulk-import/graphql/subscription/generated/jobByCreator';
import useCancelJob from '@/modules/corporate/modules/bulk-import/hooks/useCancelJob';
import useJobs from '@/modules/corporate/modules/bulk-import/hooks/useJobs';
import { JobEntityStatusEnum, JobPurposeEnum, JobSubscriptionOperationEnum } from '@/types/schema';

import { ExportJobType } from '../types';

interface JobsParams {
  purpose: JobPurposeEnum[];
  getLatestJob?: (
    jobs?: JobContentFragment[] | JobStateFragment[] | any,
  ) => JobContentFragment | undefined;
}

const JOBS_STATUS = [
  JobEntityStatusEnum.NotStarted,
  JobEntityStatusEnum.Parsing,
  JobEntityStatusEnum.Validating,
  JobEntityStatusEnum.InProcess,
];

const useExportProps = (params: JobsParams) => {
  const { purpose, getLatestJob } = params;
  const { id } = useUserContext();

  const { jobs: jobsData } = useJobs({
    purpose,
    status: JOBS_STATUS,
  });

  const latestJob = getLatestJob ? getLatestJob(jobsData) : jobsData?.[0];

  const [currentExportJob, setCurrentExportJob] = useState<ExportJobType | undefined>();

  const [{ data }] = useJobByCreatorSubscription({
    variables: {
      input: {
        createdById: id,
        purpose,
        operations: [
          JobSubscriptionOperationEnum.JobCreated,
          JobSubscriptionOperationEnum.JobUpdated,
        ],
      },
    },
  }); //try - two subscriptions reduce to one later

  const [{ data: jobData }] = useJobQuery({
    variables: { filters: { jobScheduleId: currentExportJob?.id } },
    pause: !currentExportJob?.id,
  });
  const { onCancelJob } = useCancelJob();

  const [job, setJob] = useState<JobStateFragment | undefined>();

  useEffect(() => {
    if (data && data.jobByCreator.job.jobScheduleId === currentExportJob?.id) {
      setJob(data.jobByCreator.job);
    } else if (
      currentExportJob?.id &&
      !job &&
      jobData?.job &&
      jobData.job.jobScheduleId === currentExportJob.id
    ) {
      setJob(jobData.job);
    }
    if (data && data.jobByCreator.job.purpose == JobPurposeEnum.BulkUnAssignLearnerPreprocess) {
      const latestJob = getLatestJob?.([data.jobByCreator.job]);
      if (currentExportJob?.id !== latestJob?.jobScheduleId && latestJob) {
        setCurrentExportJob({ id: latestJob.jobScheduleId });
      }
    }
    if (data && data.jobByCreator.job.purpose == JobPurposeEnum.ForceCompleteContent) {
      const latestJob = getLatestJob?.([data.jobByCreator.job]);
      if (currentExportJob?.id !== latestJob?.jobScheduleId && latestJob) {
        setCurrentExportJob({ id: latestJob.jobScheduleId });
      }
    }
  }, [currentExportJob?.id, data, job, jobData, getLatestJob, setJob]);

  useEffect(() => {
    if (!latestJob) {
      return;
    }

    setCurrentExportJob({
      id: latestJob.jobScheduleId,
    });
  }, [latestJob]);

  return {
    currentExportJob,
    setCurrentExportJob,
    job,
    onCancelJob,
    setJob,
  };
};

export default useExportProps;
